import { Classes, Icon, IconSize } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { Text, Tooltip } from 'vet-bones/components';
import { isEqual, isNotEqual } from 'vet-bones/utils';
import { useDidValueChange, usePrevious } from 'vet-bones/utils/hooks';
import { createSelectItem } from 'vet-bones/utils/select';

import { Inbound } from 'src/components/icons/Inbound';
import { Outbound } from 'src/components/icons/Outbound';
import { Link } from 'src/components/Link';
import {
  getProjectMappingLinkTestId,
  getProjectMappingRelationshipMappingMenuTestId,
  getProjectMappingRelationshipTestId,
} from 'src/constants/testIds';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { useClickModelConceptRelationshipLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptRelationshipLink';
import { useRemoveRelationshipMapping } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveRelationshipMapping';
import {
  Mapping,
  MappingIdentifier,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { ProjectClassMappingRelationshipCell } from 'src/containers/ProjectMapping/ProjectClassMappingRelationshipSection/ProjectClassMappingRelationshipCell';
import { ProjectClassMappingRelationshipMenu } from 'src/containers/ProjectMapping/ProjectClassMappingRelationshipSection/ProjectClassMappingRelationshipMenu';
import { RelationshipMappingCell } from 'src/containers/ProjectMapping/types';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { relationshipDraftChangesSelector } from 'src/selectors/draftRelationship';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectMappingsSelector } from 'src/selectors/projectMappings';
import {
  prevProjectRelationshipMappingsSelector,
  projectRelationshipMappingsSelector,
} from 'src/selectors/projectRelationshipMappings';
import * as copy from 'src/templates/copy';
import { RelationshipWithHierarchyData } from 'src/utils/getAllInheritedRelationships';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { getProjectMappingRoute } from 'src/utils/routes/getProjectMappingRoute';
import { uniqByKey } from 'src/utils/uniqByKey';

const showRelationshipIdentifierWarning = (
  cell: RelationshipMappingCell,
  relationshipMappings: RelationshipMapping[],
  allMappings: Mapping[]
) =>
  relationshipMappings.some((relMapping) => {
    const otherIdentifier =
      relMapping.fromPrimaryIdentifier || relMapping.toPrimaryIdentifier;
    const otherClassIri = relMapping.fromClassIri || relMapping.toClassIri;
    // Skip relationship mappings with missing or incomplete identifier definitions
    if (!otherIdentifier || !otherClassIri) {
      return false;
    }

    const classMapping = allMappings.find(
      (mapping) =>
        mapping.key === relMapping.to || mapping.key === relMapping.from
    );
    const classMappingIri = getModelConceptClassValue(classMapping?.to || '');

    // Ignore self-referencing relationship mapping
    if (otherClassIri === classMappingIri) {
      return false;
    }

    // Only check relationship mappings that are defining an identifier for the same class iri
    if (cell.classIri === otherClassIri) {
      return isNotEqual(cell.classMappingIdentifier, otherIdentifier);
    }

    return false;
  });

type ProjectClassMappingRelationshipRowProps = {
  direction: RelationshipDirection;
  index: number;
  relationship: RelationshipWithHierarchyData;
};

const reduceMappingKeys =
  (direction: RelationshipDirection) =>
  (acc: Set<string>, mapping: RelationshipMapping) => {
    acc.add(
      direction === RelationshipDirection.OUTGOING
        ? `${mapping.toClassIri}-${
            mapping.toPrimaryIdentifier?.baseIri
          }-${mapping.toPrimaryIdentifier?.parts.join('-')}`
        : `${mapping.fromClassIri}-${
            mapping.fromPrimaryIdentifier?.baseIri
          }-${mapping.fromPrimaryIdentifier?.parts.join('-')}`
    );
    return acc;
  };

export const ProjectClassMappingRelationshipRow: React.VFC<
  ProjectClassMappingRelationshipRowProps
> = ({ direction, index, relationship }) => {
  const classMapping = useMappingFromRoute() as Mapping & { to: string };
  const resourceId = getResourceValue(classMapping.from);
  const classIri = getModelConceptClassValue(classMapping.to);
  const { projectId } = useRouterParams();
  const navigate = useNavigate();

  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));
  const allMappings = useReduxSelector(projectMappingsSelector(projectId));
  const mappingsForResource = allMappings.filter(
    (mapping) => getResourceValue(mapping.from) === resourceId
  );
  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));
  const existingRelationshipMappings = useReduxSelector(
    projectRelationshipMappingsSelector(projectId, {
      relationshipIri: relationship.iri,
      sourceKey:
        direction === RelationshipDirection.OUTGOING
          ? classMapping.key
          : undefined,
      targetKey:
        direction === RelationshipDirection.INCOMING
          ? classMapping.key
          : undefined,
    })
  );
  const prevExistingRelationshipMappings = useReduxSelector(
    prevProjectRelationshipMappingsSelector(projectId, {
      relationshipIri: relationship.iri,
      sourceKey:
        direction === RelationshipDirection.OUTGOING
          ? classMapping.key
          : undefined,
      targetKey:
        direction === RelationshipDirection.INCOMING
          ? classMapping.key
          : undefined,
    })
  );
  const allRelationshipMappings = useReduxSelector(
    projectRelationshipMappingsSelector(projectId, {
      relationshipIri: relationship.iri,
      sourceIri: classIri,
      targetIri: classIri,
    })
  );
  // Filter out relationship mappings for this row from all relationship mappings
  const otherRelationshipMappings = allRelationshipMappings.filter(
    (relMapping) =>
      // must be from the same resource
      (relMapping.from?.includes(resourceId) ||
        relMapping.to?.includes(resourceId)) &&
      // must not be one displayed for this row
      !existingRelationshipMappings.some(
        (existingMapping) => relMapping.value === existingMapping.value
      ) &&
      // must not be self-referencing
      (relMapping.fromClassIri || relMapping.toClassIri) !==
        getModelConceptClassValue(
          allMappings.find(
            (mapping) => (relMapping.to || relMapping.from) === mapping.key
          )?.to || ''
        )
  );

  const handleClickClass = useClickModelConceptClassLink();
  const handleClickRelationshipLink = useClickModelConceptRelationshipLink();
  const removeRelationshipMapping = useRemoveRelationshipMapping();

  const initialExistingCells = React.useMemo(
    () =>
      existingRelationshipMappings.map<RelationshipMappingCell>(
        (relationshipMapping) => {
          const isSource = direction === RelationshipDirection.INCOMING;

          const emptyMappingIdentifier = { baseIri: '', parts: [] };
          let classIri: string | null = null;
          let classMappingIdentifier: MappingIdentifier =
            emptyMappingIdentifier;

          if (isSource) {
            classIri = relationshipMapping.fromClassIri || null;
            classMappingIdentifier =
              relationshipMapping.fromPrimaryIdentifier ||
              emptyMappingIdentifier;
          } else {
            classIri = relationshipMapping.toClassIri || null;
            classMappingIdentifier =
              relationshipMapping.toPrimaryIdentifier || emptyMappingIdentifier;
          }

          return {
            classIri,
            classMappingIdentifier,
            isSource,
            mappingId: relationshipMapping.value,
            relationshipMappingKey: relationshipMapping.key,
          };
        }
      ),
    [direction, existingRelationshipMappings]
  );

  const emptyCells: RelationshipMappingCell[] = React.useMemo(
    () => [
      {
        classIri: null,
        classMappingIdentifier: { baseIri: '', parts: [] },
        isSource: direction === RelationshipDirection.INCOMING,
        mappingId: uuid(),
        relationshipMappingKey: null,
      },
    ],
    [direction]
  );

  // cells need to hold form data, even if they're disassociated with
  // a relationship mapping
  const [cells, setCells] = React.useState<RelationshipMappingCell[]>(() => {
    return existingRelationshipMappings.length
      ? initialExistingCells
      : emptyCells;
  });

  const needsReset = isDraftDiagram;
  const didNeedsResetChange = useDidValueChange(needsReset);
  const prevExistingCells = usePrevious(initialExistingCells);
  const didExistingCellsChange = useDidValueChange(
    initialExistingCells,
    isEqual
  );
  const didCellsChange = useDidValueChange(cells, isEqual);
  React.useEffect(() => {
    // update cells after entering or leaving draft mode
    if (didNeedsResetChange) {
      setCells(initialExistingCells.length ? initialExistingCells : emptyCells);
      return;
    }

    // add new cells when creating relationship mappings automatically for valid classes
    if (didExistingCellsChange && !didCellsChange) {
      const prevExistingCellIds = (prevExistingCells || []).reduce(
        (acc, cell) => {
          acc.add(cell.mappingId);
          return acc;
        },
        new Set<string>()
      );
      const addedCells = initialExistingCells.filter(
        (cell) => !prevExistingCellIds.has(cell.mappingId)
      );
      setCells((prevCells) => [...addedCells, ...prevCells]);
    }
  }, [
    cells,
    didCellsChange,
    didExistingCellsChange,
    didNeedsResetChange,
    emptyCells,
    initialExistingCells,
    prevExistingCells,
  ]);

  const handleDuplicateCell = React.useCallback(
    (idx) => {
      const newCells = [...cells];
      const duplicatedCell = cells[idx];
      if (!duplicatedCell) {
        return;
      }

      const newCell: RelationshipMappingCell = {
        classIri: null,
        classMappingIdentifier: { baseIri: '', parts: [] },
        isSource: duplicatedCell.isSource,
        mappingId: uuid(),
        relationshipMappingKey: null,
      };
      newCells.splice(idx + 1, 0, newCell);
      setCells(newCells);
    },
    [cells]
  );
  const handleRemoveCell = React.useCallback(
    (idx) => {
      if (cells.length < 2) {
        return;
      }
      const newCells = [...cells];
      const cellToBeRemoved = newCells[idx];

      if (!cellToBeRemoved) {
        return;
      }

      const { relationshipMappingKey } = cellToBeRemoved;
      if (relationshipMappingKey) {
        const existingRelMapping = existingRelationshipMappings.find(
          (relMapping) => relMapping.key === relationshipMappingKey
        );
        if (existingRelMapping) {
          removeRelationshipMapping(relationshipMappingKey);
        }
      }

      newCells.splice(idx, 1);
      setCells(newCells);
    },
    [cells, existingRelationshipMappings, removeRelationshipMapping]
  );

  const { isDescriptionModified, isLabelModified, addedSources, addedTargets } =
    useReduxSelector(
      relationshipDraftChangesSelector(projectId, relationship.iri),
      isEqual
    );

  const existingKeys = existingRelationshipMappings.reduce<Set<string>>(
    reduceMappingKeys(direction),
    new Set()
  );
  const prevExistingKeys = prevExistingRelationshipMappings.reduce<Set<string>>(
    reduceMappingKeys(direction),
    new Set()
  );

  const highlightRelationship =
    isDraftDiagram &&
    Boolean(
      isDescriptionModified ||
        isLabelModified ||
        addedSources.size ||
        addedTargets.size ||
        isNotEqual(existingKeys, prevExistingKeys)
    );

  return (
    <>
      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--relationship',
          'sd-project-mapping-grid-cell--border-right',
          'sd-draft-highlight-candidate',
          {
            'sd-project-mapping-grid-cell--no-border': cells.length > 1,
            'sd-draft-highlight-candidate--highlighted': highlightRelationship,
          }
        )}
        data-testid={getProjectMappingRelationshipTestId(
          relationship.iri,
          direction,
          index
        )}
      >
        <div className="sd-project-mapping-grid-cell-left">
          <Icon
            icon={
              direction === RelationshipDirection.INCOMING ? (
                <Inbound iconSize={IconSize.LARGE} />
              ) : (
                <Outbound iconSize={IconSize.LARGE} />
              )
            }
            size={IconSize.LARGE}
          />
          <div className="sd-project-mapping-grid-cell-left-label">
            <Link
              onClick={() =>
                handleClickRelationshipLink(
                  createSelectItem(relationship.iri, relationship.iri)
                )
              }
              testId={getProjectMappingLinkTestId(relationship.iri)}
            >
              <Text ellipsize>{relationship.label}</Text>
            </Link>
            {/* Show inherited mappings */}
            {relationship.depth === 1 ? null : (
              <Text
                className={classNames(
                  'sd-project-mapping-grid-cell-left-label-subtext',
                  Classes.TEXT_SMALL
                )}
                ellipsize
              >
                {copy.components.projectMapping.relationships.inheritedFrom}
                &nbsp;
                {Array.from(relationship.sourceClassIris).map(
                  (classIri, idx) => (
                    <div key={classIri}>
                      {idx !== 0 ? <>,&nbsp;</> : null}
                      <Link
                        key={classIri}
                        onClick={() =>
                          handleClickClass(createSelectItem(classIri, classIri))
                        }
                        testId={getProjectMappingLinkTestId(classIri)}
                      >
                        <Text ellipsize>
                          {getLabelForIri(labelsByIri, classIri)}
                        </Text>
                      </Link>
                    </div>
                  )
                )}
              </Text>
            )}
            {/* Show existing mapped relationships */}
            {otherRelationshipMappings.length ? (
              <>
                <Text
                  className={classNames(
                    'sd-project-mapping-grid-cell-left-label-subtext',
                    Classes.TEXT_SMALL
                  )}
                  ellipsize
                >
                  {copy.components.projectMapping.relationships.alsoMappedIn}
                  &nbsp;
                  {allMappings
                    .filter((mapping) =>
                      Boolean(
                        otherRelationshipMappings.find(
                          ({ from, to }) =>
                            from === mapping.key || to === mapping.key
                        )
                      )
                    )
                    .map((mapping, idx) => (
                      <React.Fragment key={mapping.value}>
                        {idx ? <span>,&nbsp;</span> : null}
                        <Link
                          onClick={() =>
                            navigate(
                              getProjectMappingRoute(projectId, mapping.value)
                            )
                          }
                          testId={getProjectMappingLinkTestId(
                            mapping.value || ''
                          )}
                        >
                          <Text ellipsize>{mapping.text}</Text>
                        </Link>
                      </React.Fragment>
                    ))}
                  {cells.some((cell) =>
                    showRelationshipIdentifierWarning(
                      cell,
                      otherRelationshipMappings,
                      allMappings
                    )
                  ) ? (
                    <>
                      &nbsp;
                      <Tooltip
                        content={
                          cells[0].isSource
                            ? copy.components.projectMapping.relationships
                                .sourceWarning
                            : copy.components.projectMapping.relationships
                                .targetWarning
                        }
                      >
                        <Icon icon={IconNames.WarningSign} />
                      </Tooltip>
                    </>
                  ) : null}
                </Text>
              </>
            ) : null}
            {/* Show existing source/target class mappings */}
            {cells.some((cell) => cell.classIri) ? (
              <>
                {uniqByKey(cells, 'classIri').map((cell) => {
                  if (!cell.classIri) {
                    return null;
                  }
                  const { classIri, isSource } = cell;
                  const mappings = mappingsForResource.filter(
                    (mapping) =>
                      getModelConceptClassValue(mapping.to || '') === classIri
                  );
                  if (!mappings.length) {
                    return null;
                  }
                  const classLabel = getLabelForIri(labelsByIri, classIri);
                  const newMappings = mappings.map((mapping, idx) => (
                    <React.Fragment key={mapping.key}>
                      {idx ? <span>,&nbsp;</span> : null}
                      <Link
                        key={mapping.key}
                        onClick={() =>
                          navigate(
                            getProjectMappingRoute(projectId, mapping.value)
                          )
                        }
                        testId={getProjectMappingLinkTestId(mapping.key)}
                      >
                        <Text ellipsize>{mapping.text}</Text>
                      </Link>
                    </React.Fragment>
                  ));

                  return (
                    <Text
                      key={classIri}
                      className={classNames(
                        'sd-project-mapping-grid-cell-left-label-subtext',
                        Classes.TEXT_SMALL
                      )}
                      ellipsize
                    >
                      {copy.components.projectMapping.relationships.classSelector(
                        isSource
                      )}
                      &nbsp;
                      <Link
                        onClick={() => {
                          handleClickClass({
                            text: classLabel,
                            value: classIri || '',
                          });
                        }}
                      >
                        <Text ellipsize>{classLabel}</Text>
                      </Link>
                      &nbsp;
                      {copy.components.projectMapping.relationships.mappedIn}
                      &nbsp;
                      {newMappings}
                      {cells
                        .filter((cell) => cell.classIri === classIri)
                        .some((cell) =>
                          showRelationshipIdentifierWarning(
                            cell,
                            otherRelationshipMappings,
                            allMappings
                          )
                        ) ? (
                        // eslint-disable-next-line react/jsx-indent
                        <>
                          &nbsp;
                          <Tooltip
                            content={
                              isSource
                                ? copy.components.projectMapping.relationships
                                    .sourceWarning
                                : copy.components.projectMapping.relationships
                                    .targetWarning
                            }
                          >
                            <Icon icon={IconNames.WarningSign} />
                          </Tooltip>
                        </>
                      ) : null}
                    </Text>
                  );
                })}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {cells.map((cell, idx) => {
        const setCell = (partialCell: Partial<RelationshipMappingCell>) => {
          if (!cells[idx]) {
            return;
          }

          const newCell: RelationshipMappingCell = {
            ...cells[idx],
            ...partialCell,
          };
          const newCells = [...cells];
          newCells.splice(idx, 1, newCell);
          setCells(newCells);
        };

        const highligted =
          isDraftDiagram &&
          cell.classIri &&
          !prevExistingRelationshipMappings.find((mapping) =>
            direction === RelationshipDirection.OUTGOING
              ? mapping.toClassIri === cell.classIri &&
                isEqual(
                  mapping.toPrimaryIdentifier,
                  cell.classMappingIdentifier
                )
              : mapping.fromClassIri === cell.classIri &&
                isEqual(
                  mapping.fromPrimaryIdentifier,
                  cell.classMappingIdentifier
                )
          );

        return (
          // there really is nothing useful to use as key
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={idx}>
            {idx !== 0 ? (
              <div
                className={classNames(
                  'sd-project-mapping-grid-cell',
                  'sd-project-mapping-grid-cell--relationship',
                  'sd-project-mapping-grid-cell--border-right',
                  {
                    'sd-project-mapping-grid-cell--no-border':
                      idx !== cells.length - 1,
                  }
                )}
              />
            ) : null}
            <div
              className={classNames(
                'sd-project-mapping-grid-cell',
                'sd-project-mapping-grid-cell--semi-wide',
                'sd-draft-highlight-candidate',
                {
                  'sd-draft-highlight-candidate--highlighted': highligted,
                }
              )}
            >
              <ProjectClassMappingRelationshipCell
                relationship={relationship}
                relationshipCellData={cell}
                setCell={setCell}
              />
            </div>
            <div
              className={classNames(
                'sd-project-mapping-grid-cell',
                'sd-draft-highlight-candidate',
                {
                  'sd-draft-highlight-candidate--highlighted': highligted,
                }
              )}
              data-testid={getProjectMappingRelationshipMappingMenuTestId(
                relationship.iri,
                direction,
                cell.relationshipMappingKey || '',
                idx
              )}
            >
              <ProjectClassMappingRelationshipMenu
                onDuplicate={() => handleDuplicateCell(idx)}
                onRemove={
                  cells.length < 2 ? undefined : () => handleRemoveCell(idx)
                }
              />
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
};
